
import { computed, inject } from 'vue';
import { NodeType } from '@amplify/amp-module';
import NodeAction from '@/components/graph/common/NodeAction.vue';

export default {
  components: { NodeAction },

  props: {
    /**
     * nodeId - indicates the node id
     * nodeType - indicates the node type
     * subNodeId - indicates the sub node id. If defined, the nodeId represents the id of the group node
     * actions - array with the wanted actions
     * * should contain objects looking like: { key: 'bottom', handler:() => {}, click: Boolean }
     * * key - indicates the type and position of the action
     * * click - provides default functionality (add addingNode or open contextMenu)
     * * handler - takes precedence and overwrites default functionality
     * showActions - indicates if the actions should be displayed
     */
    nodeId: { type: String, default: '' },
    nodeType: { type: String, default: '' },
    subNodeId: { type: String, default: '' },
    actions: { type: Array, default: () => [{ key: 'bottom', click: true }] },
    showActions: { type: Boolean, default: false },
  },

  setup(props) {
    const getNode = inject('getNode', () => {});

    const actionsKeys = computed(() => props.actions.map((action) => action.key));

    const defaultPadding = 32;
    const topPadding = defaultPadding + 12;

    // align the action button on the edge start line
    const bottomPadding = computed(() => {
      const parentNodeType = getNode().shape.split('-')[0];

      // sub nodes inside a button node
      if (!!props.subNodeId && parentNodeType === NodeType.buttonGroup) {
        return defaultPadding + 16;
      }

      // sub nodes inside a question node
      if (!!props.subNodeId && parentNodeType === NodeType.question) {
        return defaultPadding + 32;
      }

      return defaultPadding;
    });

    const additionalHeight = computed(() => {
      let height = 0;

      if (actionsKeys.value.includes('top')) {
        height += topPadding;
      }

      if (actionsKeys.value.includes('bottom')) {
        height += bottomPadding.value;
      }

      return height;
    });

    const additionalWidth = computed(() => {
      let width = 0;

      if (actionsKeys.value.includes('left')) {
        width += defaultPadding;
      }

      if (actionsKeys.value.includes('right')) {
        width += defaultPadding;
      }

      return width;
    });

    // adjust actions container size and position
    const actionsContainerStyle = computed(() => {
      let top = 0;
      let left = 0;

      if (actionsKeys.value.includes('top')) {
        top = topPadding * -1;
      }

      if (actionsKeys.value.includes('left')) {
        left = defaultPadding * -1;
      }

      return {
        width: `calc(100% + ${additionalWidth.value}px)`,
        height: `calc(100% + ${additionalHeight.value}px)`,
        top: top + 'px',
        left: left + 'px',
      };
    });

    // adjust action size and position
    const actionAreaStyle = (side) => {
      if (side === 'top' || side === 'bottom') {
        return {
          left: actionsKeys.value.includes('left') ? defaultPadding + 'px' : 0,
          width: `calc(100% - ${additionalWidth.value}px)`,
        };
      }

      if (side === 'left' || side === 'right') {
        return {
          top: actionsKeys.value.includes('top') ? topPadding + 'px' : 0,
          height: `calc(100% - ${additionalHeight.value}px)`,
        };
      }

      return {};
    };

    return {
      actionsContainerStyle,
      actionAreaStyle,
    };
  },
};
